/**
 * Text diff utility for highlighting changes between original and modified text
 */

export interface DiffPart {
  type: 'added' | 'removed' | 'unchanged'
  value: string
}

/**
 * Simple word-level diff algorithm
 * Returns array of diff parts with type and value
 */
export function diffWords(original: string, modified: string): DiffPart[] {
  if (!original && !modified) return []
  if (!original) return [{ type: 'added', value: modified }]
  if (!modified) return [{ type: 'removed', value: original }]

  // Split by whitespace while preserving it
  const splitText = (text: string): string[] => {
    return text.split(/(\s+)/).filter(Boolean)
  }

  const originalWords = splitText(original)
  const modifiedWords = splitText(modified)

  // Simple LCS-based diff
  const matrix: number[][] = []

  // Build matrix
  for (let i = 0; i <= originalWords.length; i++) {
    matrix[i] = []
    for (let j = 0; j <= modifiedWords.length; j++) {
      if (i === 0 || j === 0) {
        matrix[i][j] = 0
      } else if (originalWords[i - 1] === modifiedWords[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1] + 1
      } else {
        matrix[i][j] = Math.max(matrix[i - 1][j], matrix[i][j - 1])
      }
    }
  }

  // Backtrack to find diff
  const result: DiffPart[] = []
  let i = originalWords.length
  let j = modifiedWords.length

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && originalWords[i - 1] === modifiedWords[j - 1]) {
      result.unshift({ type: 'unchanged', value: originalWords[i - 1] })
      i--
      j--
    } else if (j > 0 && (i === 0 || matrix[i][j - 1] >= matrix[i - 1][j])) {
      result.unshift({ type: 'added', value: modifiedWords[j - 1] })
      j--
    } else if (i > 0) {
      result.unshift({ type: 'removed', value: originalWords[i - 1] })
      i--
    }
  }

  // Merge consecutive same-type parts
  const merged: DiffPart[] = []
  for (const part of result) {
    if (merged.length > 0 && merged[merged.length - 1].type === part.type) {
      merged[merged.length - 1].value += part.value
    } else {
      merged.push(part)
    }
  }

  return merged
}

/**
 * Check if there are any changes between two texts
 */
export function hasChanges(original: string, modified: string): boolean {
  return original.trim() !== modified.trim()
}
